<template>
    <div class="h-full flex flex-col space-y-4">
        <div class="flex shrink-0 items-center justify-end space-x-2">
            <div class="flex items-center space-x-2">
                <UInput
                    v-model="search"
                    type="text"
                    placeholder="Search..."
                    icon="i-ph-magnifying-glass-duotone"
                />
                <UInputMenu
                    v-model="filters.provider"
                    :search="searchProvider"
                    class="w-52"
                    placeholder="Provider"
                    option-attribute="name"
                    leading-icon="i-ph-ambulance-duotone"
                    trailing
                    nullable
                    by="id"
                />
                <USelectMenu
                    v-model="filters.status"
                    trailing
                    :class="filters.status ? 'w-44' : 'w-40'"
                    :leading-icon="filters.status ? undefined : 'i-ph-list-numbers-duotone'"
                    :ui="{
                        icon: { base: '!size-4', trailing: { pointer: 'pointer-events-auto', base: '!size-4' } }
                    }"
                    :options="[
                        { name: 'Open', value: InvoiceStatus.open, dot: 'bg-blue-500' },
                        { name: 'Settled', value: InvoiceStatus.settled, dot: 'bg-emerald-500' },
                        { name: 'Voided', value: InvoiceStatus.voided, dot: 'bg-red-500' },
                    ]"
                >
                    <template #label>
                        <template v-if="filters.status">
                            <span class="text-gray-400">Status:</span>
                            <span class="truncate font-medium">{{ filters.status.name }}</span>
                        </template>
                        <span v-else class="text-gray-400">Status</span>
                    </template>
                    <template #option="{ option }">
                        <span :class="[option.dot, 'inline-block size-1.5 mx-1 flex-shrink-0 rounded-full']" aria-hidden="true" />
                        <span class="truncate">{{ option.name }}</span>
                    </template>
                    <template #trailing v-if="filters.status">
                        <button @click.stop="filters.status = undefined" type="button" class="flex items-center justify-center">
                            <UIcon name="i-ph-x-bold" class="size-3" />
                        </button>
                    </template>
                </USelectMenu>
                <UButton
                    label="Clear"
                    color="gray"
                    @click="clearFilters()"
                    class="text-center"
                />
                <UButton @click="overlays.create = true" color="black" icon="i-ph-plus-bold">
                    Create Invoice
                </UButton>
            </div>
        </div>

        <div class="flex-1 bg-white ring-1 ring-black/5 rounded-lg shadow-sm overflow-hidden">
            <UTable
                :rows="invoices"
                :columns="columns"
                :loading="loading"
                :loading-state="{ icon: 'i-ph-circle-notch-bold', label: '' }"
                :empty-state="{ icon: 'i-ph-receipt-duotone', label: 'No provider invoices.' }"
                :ui="{ thead: 'sticky top-0 bg-white z-10 shadow-sm' }"
                class="h-full"
            >
                <template #id-data="{ row }">
                    <ULink :to="`/invoices/providers/${row.id}`" class="font-mono hover:underline">
                        {{ row.id.split('-').at(-1)?.toUpperCase() }}
                    </ULink>
                </template>
                <template #number-data="{ row }">
                    <p class="font-mono">
                        #{{ row.number.toString().padStart(6, '0') }}
                    </p>
                </template>
                <template #provider-data="{ row }">
                    <div v-if="row.provider" class="flex items-center space-x-2.5">
                        <Avatar bucket="media" folder="providers" size="sm" :placeholder="row.provider.name" :path="row.provider.avatar" />
                        <span>{{ row.provider.name }}</span>
                    </div>
                </template>
                <template #commission-data="{ row }">
                    <span class="font-mono">{{ row.commission_percentage }}%</span>
                </template>
                <template #transports-data="{ row }">
                    <p class="font-mono">
                        {{ row.transports?.length ? row.transports[0].count : 0 }}
                    </p>
                </template>
                <template #status-data="{ row }">
                    <BadgeInvoiceStatus :invoice="row as any" />
                </template>
                <template #created-data="{ row }">
                    <TableDateCell :date="row.created_at" />
                </template>
                <template #actions-data="{ row }">
                    <UDropdown :items="actions(row)">
                        <UButton color="gray" variant="ghost" icon="i-heroicons-ellipsis-horizontal-20-solid" />
                    </UDropdown>
                </template>
            </UTable>
        </div>

        <TablePagination :pagination="pagination" class="shrink-0" />

        <OverlayInvoiceCreateProviderInvoice v-model:open="overlays.create" @reload="fetchData" />
        <OverlayInvoiceSettleProviderInvoice v-model:open="overlays.settle" @reload="fetchData" :invoice="selectedInvoice" />
        <OverlayInvoiceVoidProviderInvoice v-model:open="overlays.void" @reload="fetchData" :invoice="selectedInvoice" />
    </div>
</template>

<script setup lang="ts">
import { watchDebounced } from '@vueuse/core'
import { InvoiceStatus, type ProviderInvoice } from '~/types/invoice'

useHead({ title: 'Invoices – Providers' })

const client = useSupabaseClient()
const pagination = usePagination()

const loading = ref(false)
const search = ref('')
const filters = ref<{ [key: string]: any }>({})
const invoices = ref<ProviderInvoice[]>([])
const selectedInvoice = ref<ProviderInvoice>()

const overlays = reactive({
    create: false,
    settle: false,
    void: false,
})

const columns = [
    { key: 'id', label: 'ID' },
    { key: 'number', label: 'Number' },
    { key: 'provider', label: 'Provider' },
    { key: 'commission', label: 'Commission' },
    { key: 'transports', label: 'Transports' },
    { key: 'status', label: 'Status' },
    { key: 'created', label: 'Created' },
    { key: 'actions', class: 'w-px' },
]

const actions = (invoice: ProviderInvoice) => [
    [
        {
            label: 'View',
            icon: 'i-ph-receipt-fill',
            iconClass: 'text-gray-400',
            click: () => {
                useRouter().push(`/invoices/providers/${invoice.id}`)
            },
        },
    ],
    [
        {
            label: 'Mark as Settled',
            icon: 'i-ph-check-fill',
            iconClass: 'text-emerald-400',
            disabled: !!invoice.settled_at || !!invoice.voided_at,
            click: () => {
                selectedInvoice.value = invoice
                overlays.settle = true
            },
        },
        {
            label: 'Void',
            icon: 'i-ph-x-fill',
            iconClass: 'text-red-400',
            disabled: !!invoice.settled_at || !!invoice.voided_at,
            click: () => {
                selectedInvoice.value = invoice
                overlays.void = true
            },
        },
    ],
]

onMounted(() => fetchData())

watchDebounced(search, () => fetchData(true), { debounce: 600 })
watch(filters, () => fetchData(true), { deep: true })
watch(() => pagination.from, () => fetchData())

async function fetchData(resetPagination?: boolean) {
    loading.value = true
    invoices.value = []
    pagination.setTotal(undefined)

    if (resetPagination) {
        pagination.page = 0
    }

    let query = client.from('provider_invoices')
        .select(`
            id, number, provider_id, commission_percentage, settled_at, voided_at, created_at, updated_at,
            provider:providers (id, name, avatar),
            transports (count)
        `, { count: 'exact' })
        .order('created_at', { ascending: false })
        .range(pagination.from, pagination.to)

    if (search.value) {
        if (search.value.includes(',')) {
            const numbers = search.value.split(',').filter(Boolean)
            query = query.in('number', numbers)
        } else {
            const numberQuery = !isNaN(parseInt(search.value)) ? parseInt(search.value) : undefined
            if (numberQuery) {
                query = query.eq('number', numberQuery)
            }
        }
    }

    if (filters.value.provider) {
        query = query.eq('provider_id', filters.value.provider.id)
    }

    if (filters.value.status) {
        if (filters.value.status.value === 'open') {
            query = query.is('settled_at', null).is('voided_at', null)
        } else if (filters.value.status.value === 'settled') {
            query = query.not('settled_at', 'is', null).is('voided_at', null)
        } else if (filters.value.status.value === 'voided') {
            query = query.not('voided_at', 'is', null)
        }
    }

    const { count, data } = await query

    if (count !== null && data) {
        invoices.value = data as unknown as ProviderInvoice[]
        pagination.setTotal(count)
    }

    loading.value = false
}

async function searchProvider(q: string) {
    const { data, error } = await client.from('providers')
        .select('id, name')
        .is('deleted_at', null)
        .ilike('name', `%${q}%`)
        .order('name')

    if (error) return []
    return data
}

function clearFilters() {
    search.value = ''
    filters.value = {}
}
</script>
